/**
 * Seedance 2.5 omni-reference task type hint (BytePlus ModelArk
 * `omni_reference_task_type`, top-level create-task body field).
 * Docs: https://docs.byteplus.com/en/docs/ModelArk/1520757
 *
 * - reference: no special ratio/duration constraints.
 * - edit: needs a reference_video, ratio "adaptive", duration -1.
 * - extend: needs a reference_video, ratio "adaptive" (duration is the added length).
 * - auto: model infers; mismatches fail async with InvalidParameter.TaskTypeConstraint.
 */
export const SEEDANCE_TASK_TYPES = ["auto", "reference", "edit", "extend"] as const;
export type SeedanceTaskType = (typeof SEEDANCE_TASK_TYPES)[number];

export function normalizeSeedanceTaskType(raw: unknown): SeedanceTaskType | undefined {
  if (typeof raw !== "string") return undefined;
  const v = raw.trim().toLowerCase();
  return (SEEDANCE_TASK_TYPES as readonly string[]).includes(v)
    ? (v as SeedanceTaskType)
    : undefined;
}

function sameRatio(a: string, b: string): boolean {
  const parse = (s: string) => {
    const m = s.trim().match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
    return m ? Number(m[1]) / Number(m[2]) : NaN;
  };
  const x = parse(a);
  const y = parse(b);
  if (Number.isNaN(x) || Number.isNaN(y)) return a.trim() === b.trim();
  return Math.abs(x - y) < 0.01;
}

/**
 * Resolve the task type to send. Returns undefined for "send nothing" (auto).
 * auto/omitted + match_reference + video ref + a concrete requested ratio that
 * differs from the source video (or the source ratio is unknown) => reference,
 * since edit/extend could never honour a fixed ratio.
 */
export function resolveSeedanceTaskType(input: {
  taskType?: unknown;
  referenceIntent?: string;
  hasVideoReference: boolean;
  hasStartFrame: boolean;
  requestedRatio?: string;
  sourceVideoRatio?: string;
}): Exclude<SeedanceTaskType, "auto"> | undefined {
  if (input.hasStartFrame) return undefined;
  const explicit = normalizeSeedanceTaskType(input.taskType);
  if (explicit && explicit !== "auto") return explicit;
  const ratio = input.requestedRatio?.trim();
  if (
    input.referenceIntent === "match_reference" &&
    input.hasVideoReference &&
    ratio &&
    ratio !== "adaptive" &&
    (!input.sourceVideoRatio || !sameRatio(ratio, input.sourceVideoRatio))
  ) {
    return "reference";
  }
  return undefined;
}

/** Ratio/duration to send for a resolved task type. */
export function seedanceTaskTypeConstraints(
  taskType: SeedanceTaskType | undefined,
  ratio: string | undefined,
  duration: number,
): { ratio: string | undefined; duration: number } {
  if (taskType === "edit") return { ratio: "adaptive", duration: -1 };
  if (taskType === "extend") return { ratio: "adaptive", duration };
  return { ratio, duration };
}
